import { Injectable } from '@angular/core';
import { CurrencyData } from '@cpak/interfaces';
import Decimal from 'decimal.js';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable()
export class CurrencyService {
  private baseToLocalExchangeRate = -1;
  private baseToLocalParentExchangeRate = -1;
  private initialCurrencyData: CurrencyData = {
    localCurrency: '',
    baseCurrency: '',
    baseCurrencyLabel: '',
    localCurrencyLabel: '',
    exchangeRate: -1,
    parentExchangeRate: -1,
    exchangeRateLabel: '',
    lastExchangeRateLabel: '',
    selectedCurrency: '',
  };
  private currencyData$ = new BehaviorSubject<CurrencyData>(this.initialCurrencyData);

  public getBaseToLocalExchangeRate(): number {
    return this.baseToLocalExchangeRate;
  }

  public resetCurrencyData(): void {
    this.currencyData$.next(this.initialCurrencyData);
    this.baseToLocalExchangeRate = -1;
  }

  public shouldPricingModelBeConverted(): boolean {
    const currencyData = this.currencyData$.getValue();
    return (
      currencyData.selectedCurrency === currencyData.localCurrency &&
      currencyData.baseCurrency !== currencyData.localCurrency
    );
  }

  public isBaseOrLocalPricingModel(): string {
    const currencyData = this.currencyData$.getValue();
    if (currencyData.selectedCurrency === currencyData.baseCurrency) {
      return 'USD';
    } else {
      return 'local';
    }
  }

  public setCurrencyData(data: Partial<CurrencyData>): void {
    if (data.exchangeRate) {
      const currentCurrencyData = this.currencyData$.getValue();
      let newCurrencyData = { ...currentCurrencyData, ...data };
      if (this.baseToLocalExchangeRate === -1) {
        this.baseToLocalExchangeRate = newCurrencyData.exchangeRate;
        this.baseToLocalParentExchangeRate = newCurrencyData.parentExchangeRate;
        newCurrencyData =
          newCurrencyData.selectedCurrency === newCurrencyData.baseCurrency
            ? this.getRateAndLabels(newCurrencyData)
            : this.getRateAndLabels(newCurrencyData, true);
      } else {
        newCurrencyData =
          newCurrencyData.selectedCurrency === currentCurrencyData.baseCurrency
            ? this.getRateAndLabels(newCurrencyData)
            : this.getRateAndLabels(newCurrencyData, true);
      }

      if (newCurrencyData.baseCurrency) {
        newCurrencyData.baseCurrencyLabel = this.getBaseCurrencyLabel(newCurrencyData.baseCurrency);
      }
      if (newCurrencyData.localCurrency) {
        newCurrencyData.localCurrencyLabel = this.getLocalCurrencyLabel(newCurrencyData.localCurrency);
      }

      this.currencyData$.next(newCurrencyData);
    }
  }

  public getCurrencyData(): Observable<CurrencyData> {
    return this.currencyData$.asObservable();
  }

  private getLocalCurrencyLabel(currency: string): string {
    return `Local (${currency})`;
  }

  private getBaseCurrencyLabel(currency: string): string {
    return `Base (${currency})`;
  }

  private getRateAndLabels(currencyData: CurrencyData, isLocalCurrency = false): CurrencyData {
    return isLocalCurrency
      ? {
          ...currencyData,
          exchangeRate: Decimal.div(1, this.baseToLocalExchangeRate).toNumber(),
          parentExchangeRate: Decimal.div(1, this.baseToLocalParentExchangeRate).toNumber(),
          exchangeRateLabel: `${currencyData.baseCurrency}/${currencyData.localCurrency}`,
          lastExchangeRateLabel: `${currencyData.localCurrency}/${currencyData.baseCurrency}`,
        }
      : {
          ...currencyData,
          exchangeRate: this.baseToLocalExchangeRate,
          parentExchangeRate: this.baseToLocalParentExchangeRate,
          exchangeRateLabel: `${currencyData.localCurrency}/${currencyData.baseCurrency}`,
          lastExchangeRateLabel: `${currencyData.baseCurrency}/${currencyData.localCurrency}`,
        };
  }
}
